import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import type { PaymentCreate } from '#/shared/api/generated/billing/models'
import { useCreatePaymentApiV1PaymentsPost } from '#/shared/api/generated/billing/theCargoBilling'
import type {
  ContractCreateBodyContractType,
  TaskCreateBodyPeriod,
  TaskCreateBodyPriority,
  TaskCreateBodyType,
  UserFileTag,
} from '#/shared/api/generated/shipment/models'
import {
  useCreateItemApiV1ToolbarPost,
  useUploadFileApiV1ToolbarUploadPost,
} from '#/shared/api/generated/shipment/theCargoShipment'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { sanitizeSmsText } from '#/shared/lib/sms-sanitize'
import { toast } from '#/shared/ui/sonner'
import { PAYMENT_STATUS } from '#/shared/ui/create-payment-modal'
import type {
  PaymentDirection,
  PaymentMethod,
  PaymentType,
} from '#/shared/ui/create-payment-modal'

import { TOOLBAR_KIND, TOOLBAR_QUERY_KEY_PREFIX } from './toolbar-kinds'

interface CreateNoteInput {
  text: string
  pinned?: boolean
  rating?: 1 | 2 | 3 | 4 | 5
  mentionedUserIds?: string[]
}

interface CreateTaskInput {
  title: string
  type?: TaskCreateBodyType
  priority?: TaskCreateBodyPriority
  date?: string | null
  startTime?: string | null
  endTime?: string | null
  period?: TaskCreateBodyPeriod
  assigneeIds?: string[]
}

interface CreateContractInput {
  contractType: ContractCreateBodyContractType
}

interface CreateSmsInput {
  toPhones: string[]
  body: string
  fromPhone?: string
  customerId?: string
  templateId?: string
}

interface UploadFileInput {
  file: File
  displayName?: string
  tag?: UserFileTag
  isOrderFile?: boolean
}

interface CreatePaymentInput {
  paymentType: PaymentType
  direction: PaymentDirection
  amount: number
  surcharge?: number | null
  discount?: number | null
  method?: PaymentMethod
  methodIds?: string[]
  dueDate?: string
  memo?: string
  reference?: string
}

interface CreateEmailInput {
  to: string[]
  cc?: string[]
  bcc?: string[]
  replyTo?: string
  subject?: string
  body?: string
  fromEmail?: string
  templateId?: string
  templateKey?: string
  attachedFileIds?: string[]
}

function trimNonEmpty(value: string | undefined): string | undefined {
  if (!value) return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

function computeTotalAmount(input: CreatePaymentInput): number {
  const surchargeAmount = (input.amount * (input.surcharge ?? 0)) / 100
  return Math.max(0, input.amount + surchargeAmount - (input.discount ?? 0))
}

function rejectMissingShipment() {
  return Promise.reject(new Error('Missing shipment id'))
}

function useToolbarItemMutation(
  successKey: string | null = 'shipmentToolbar.create.success',
) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  return useCreateItemApiV1ToolbarPost({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          predicate: (query) => {
            const head = query.queryKey[0]
            return (
              typeof head === 'string' &&
              head.startsWith(TOOLBAR_QUERY_KEY_PREFIX)
            )
          },
        })
        if (successKey) toast.success(t(successKey))
      },
      onError: (error) => {
        toast.error(
          getApiErrorDetail(error) ?? t('shipmentToolbar.create.error'),
        )
      },
    },
  })
}

const PAYMENTS_QUERY_KEY_PREFIX = '/api/v1/payments'

function usePaymentMutation() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  return useCreatePaymentApiV1PaymentsPost({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          predicate: (query) => {
            const head = query.queryKey[0]
            if (typeof head !== 'string') return false
            return (
              head.startsWith(TOOLBAR_QUERY_KEY_PREFIX) ||
              head.startsWith(PAYMENTS_QUERY_KEY_PREFIX)
            )
          },
        })
        toast.success(t('shipmentToolbar.create.paymentSuccess'))
      },
      onError: (error) => {
        toast.error(
          getApiErrorDetail(error) ?? t('shipmentToolbar.create.error'),
        )
      },
    },
  })
}

function useFileUploadMutation() {
  const { t } = useTranslation()
  return useUploadFileApiV1ToolbarUploadPost({
    mutation: {
      onError: (error) => {
        toast.error(
          getApiErrorDetail(error) ?? t('shipmentToolbar.create.error'),
        )
      },
    },
  })
}

function useCreateNote(shipmentId: string | undefined) {
  const mutation = useToolbarItemMutation('shipmentToolbar.create.noteSuccess')
  const create = useCallback(
    (input: CreateNoteInput) => {
      if (!shipmentId) return rejectMissingShipment()
      return mutation.mutateAsync({
        data: {
          kind: TOOLBAR_KIND.note,
          shipment_id: shipmentId,
          text: input.text,
          pinned: input.pinned,
          rating: input.rating,
          mentioned_user_ids: input.mentionedUserIds,
        },
      })
    },
    [mutation, shipmentId],
  )
  return { create, isPending: mutation.isPending }
}

function useCreateTask(shipmentId: string | undefined) {
  const mutation = useToolbarItemMutation('shipmentToolbar.create.taskSuccess')
  const create = useCallback(
    (input: CreateTaskInput) => {
      if (!shipmentId) return rejectMissingShipment()
      return mutation.mutateAsync({
        data: {
          kind: TOOLBAR_KIND.task,
          shipment_id: shipmentId,
          title: input.title,
          type: input.type,
          priority: input.priority,
          date: input.date,
          start_time: input.startTime,
          end_time: input.endTime,
          period: input.period,
          assignee_ids: input.assigneeIds,
        },
      })
    },
    [mutation, shipmentId],
  )
  return { create, isPending: mutation.isPending }
}

function useCreateContract(shipmentId: string | undefined) {
  const mutation = useToolbarItemMutation(null)
  const create = useCallback(
    (input: CreateContractInput) => {
      if (!shipmentId) return rejectMissingShipment()
      return mutation.mutateAsync({
        data: {
          kind: TOOLBAR_KIND.contract,
          shipment_id: shipmentId,
          contract_type: input.contractType,
        },
      })
    },
    [mutation, shipmentId],
  )
  return { create, isPending: mutation.isPending }
}

function useCreateSms(shipmentId: string | undefined) {
  const mutation = useToolbarItemMutation('shipmentToolbar.create.smsSuccess')
  const create = useCallback(
    (input: CreateSmsInput) => {
      if (!shipmentId) return rejectMissingShipment()
      return mutation.mutateAsync({
        data: {
          kind: TOOLBAR_KIND.sms,
          shipment_id: shipmentId,
          to_phones: input.toPhones,
          // Sanitize on the way out no matter which surface built the body —
          // fancy Unicode font gets spam-blocked by Telnyx.
          body: sanitizeSmsText(input.body),
          from_phone: input.fromPhone,
          customer_id: input.customerId,
          template_id: input.templateId,
        },
      })
    },
    [mutation, shipmentId],
  )
  return { create, isPending: mutation.isPending }
}

function useUploadFile(shipmentId: string | undefined) {
  const fileMutation = useToolbarItemMutation(
    'shipmentToolbar.create.fileSuccess',
  )
  const fileUploadMutation = useFileUploadMutation()
  const create = useCallback(
    async (input: UploadFileInput) => {
      if (!shipmentId) return rejectMissingShipment()
      const upload = await fileUploadMutation.mutateAsync({
        data: {
          file: input.file as unknown as string,
          shipment_id: shipmentId,
        },
      })
      const result = upload.data
      return fileMutation.mutateAsync({
        data: {
          kind: TOOLBAR_KIND.file,
          shipment_id: shipmentId,
          name: trimNonEmpty(input.displayName) ?? result.name,
          url: result.url,
          mime_type: result.mime_type,
          size_bytes: result.size_bytes,
          tag: input.tag,
          is_order_file: input.isOrderFile,
        },
      })
    },
    [fileMutation, fileUploadMutation, shipmentId],
  )
  return {
    create,
    isPending: fileUploadMutation.isPending || fileMutation.isPending,
  }
}

function useCreatePayment(shipmentId: string | undefined) {
  const mutation = usePaymentMutation()
  const create = useCallback(
    (input: CreatePaymentInput) => {
      if (!shipmentId) return rejectMissingShipment()
      return mutation.mutateAsync({
        data: {
          shipment_id: shipmentId,
          payment_type: input.paymentType,
          direction: input.direction,
          amount: input.amount,
          discount: input.discount,
          surcharge_fee_rate: input.surcharge,
          total_amount: computeTotalAmount(input),
          charge_type: input.method,
          method_ids: input.methodIds,
          expires_at: input.dueDate,
          memo: input.memo,
          reference: input.reference,
          status: PAYMENT_STATUS.created,
          payment_method: input.method,
        } as PaymentCreate & { payment_method?: string },
      })
    },
    [mutation, shipmentId],
  )
  return { create, isPending: mutation.isPending }
}

function useCreateEmail(shipmentId: string | undefined) {
  const mutation = useToolbarItemMutation('shipmentToolbar.create.emailSuccess')
  const create = useCallback(
    (input: CreateEmailInput) => {
      if (!shipmentId) return rejectMissingShipment()
      return mutation.mutateAsync({
        data: {
          kind: TOOLBAR_KIND.email,
          shipment_id: shipmentId,
          to: input.to,
          cc: input.cc,
          bcc: input.bcc,
          reply_to: input.replyTo,
          subject: input.subject,
          body: input.body,
          from_email: input.fromEmail,
          template_id: input.templateId,
          template_key: input.templateKey,
          attached_file_ids: input.attachedFileIds,
        },
      })
    },
    [mutation, shipmentId],
  )
  return { create, isPending: mutation.isPending }
}

export function useCreateToolbarItem(shipmentId: string | undefined) {
  const note = useCreateNote(shipmentId)
  const task = useCreateTask(shipmentId)
  const contract = useCreateContract(shipmentId)
  const sms = useCreateSms(shipmentId)
  const file = useUploadFile(shipmentId)
  const payment = useCreatePayment(shipmentId)
  const email = useCreateEmail(shipmentId)

  return {
    createNote: note.create,
    isNotePending: note.isPending,
    createTask: task.create,
    isTaskPending: task.isPending,
    createContract: contract.create,
    isContractPending: contract.isPending,
    createSms: sms.create,
    isSmsPending: sms.isPending,
    uploadFile: file.create,
    isFileUploading: file.isPending,
    createEmail: email.create,
    isEmailPending: email.isPending,
    createPayment: payment.create,
    isPaymentPending: payment.isPending,
  }
}
